import React, {useEffect} from 'react';

import appConfig from '@/config/appConfig.typed';
import amplitudeHomePageTracking from '@/services/analytics/amplitude/amplitudeHomePageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {filterSectionsById} from '@/services/pageSectionService';
import {useDynamicMobileHighlights} from '@/views/Highlights/components/MobileHighlights/hooks/useDynamicMobileHighlights';

import {useHighlightsSectionId} from '../../contexts/HighlightsSectionIdContext';
import useHighlightsPushNotification from '../../hooks/useHighlightsPushNotification';
import useHighlightsState from '../../hooks/useHighlightsState';
import {findHighlightIndexByTag} from '../../utils/highlightsUtils';
import HighlightsContainer from '../common/components/HighlightsContainer';
import HighlightsHeader from '../common/components/HighlightsHeader';
import FullscreenHighlightsCarousel from './components/FullscreenHighlightsCarousel';
import HighlightsCarousel from './components/HighlightsCarousel';

const getHighlights = (id) => {
    const highlightsFromConfig = appConfig.getHighlights();
    const highlights = filterSectionsById({list: highlightsFromConfig, id});

    return highlights?.sort(({banner: a}, {banner: b}) => a?.order - b?.order);
};

const MobileHighlights = () => {
    const id = useHighlightsSectionId();
    const {isFullscreen, setIsFullscreen, currentIndex, setCurrentIndex} = useHighlightsState();

    const highlights = useDynamicMobileHighlights(getHighlights(id));
    const highlightsAutoplay = appConfig.getHighlightsAutoplay();

    const {
        shouldOpenFullscreen: shouldOpenFromNotification,
        globalIndex,
        notificationTrigger,
    } = useHighlightsPushNotification((tag) => findHighlightIndexByTag(highlights, tag));

    // Open fullscreen when triggered by push notification
    useEffect(() => {
        if (shouldOpenFromNotification) {
            setIsFullscreen(true);
            setCurrentIndex(globalIndex);
        }
    }, [notificationTrigger, shouldOpenFromNotification, globalIndex, setIsFullscreen, setCurrentIndex]);

    const trackBannerClick = () => {
        const currentHighlight = highlights[currentIndex];

        if (!currentHighlight) {
            return null;
        }

        const trackingValue = currentHighlight.banner?.amplitudeTrackingValue;

        amplitudeHomePageTracking.trackHighlightBannerClick(trackingValue);
        googleAnalyticsService.trackHighlightsBannerClick(trackingValue);
    };

    const onSlideClick = () => {
        trackBannerClick();
        setIsFullscreen(true);
    };
    const onSlideChange = (index) => {
        setCurrentIndex(index);
    };

    if (!highlights?.length) return;

    return (
        <HighlightsContainer>
            {isFullscreen ? (
                <FullscreenHighlightsCarousel
                    setIsFullscreen={setIsFullscreen}
                    currentIndex={currentIndex}
                    highlights={highlights}
                    autoplay={highlightsAutoplay}
                    onSlideChange={onSlideChange}
                    isInfinite
                />
            ) : (
                <>
                    <HighlightsHeader />
                    <HighlightsCarousel
                        initialSlide={currentIndex}
                        currentIndex={currentIndex}
                        onSlideClick={onSlideClick}
                        highlights={highlights}
                        autoplay={highlightsAutoplay}
                        onSlideChange={onSlideChange}
                        isInfinite
                    />
                </>
            )}
        </HighlightsContainer>
    );
};

export default MobileHighlights;
